import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import './index.css'
import { AuthProvider } from 'react-oidc-context'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import './util/scope'
import { initOtel } from './otel'
import { mainRoutes, signinRoutes } from './routes'
import userManager from './io/api/authorization/userManager'

const stage = import.meta.env.VITE_STAGE
const dsn = 'https://8dc821e5d3594e9c4eaabfe634b1dcf1@o232550.ingest.us.sentry.io/4508138519134208'

const logToSentry = !!import.meta.env.VITE_CI

initOtel()

Sentry.init({
  dsn: logToSentry ? dsn : '',
  integrations: [Sentry.captureConsoleIntegration({ levels: ['error'] })],
  environment: stage,
})

const onUserLoaded = () => console.debug('[AuthDebug] userLoaded')
const onUserSignedIn = () => console.debug('[AuthDebug] userSignedIn')
const onSilentRenewError = (error: Error) => console.error(`[AuthDebug] silentRenewError: ${error.message}`)

userManager.events.addUserLoaded(onUserLoaded)
userManager.events.addUserSignedIn(onUserSignedIn)
userManager.events.addSilentRenewError(onSilentRenewError)

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    userManager.events.removeUserLoaded(onUserLoaded)
    userManager.events.removeUserSignedIn(onUserSignedIn)
    userManager.events.removeSilentRenewError(onSilentRenewError)
  })
}

const signinRouter = createBrowserRouter(signinRoutes)
const mainRouter = createBrowserRouter(mainRoutes)

const isSignInPath = window.location.pathname === '/signin'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 0,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {isSignInPath ? (
      <RouterProvider router={signinRouter} />
    ) : (
      <AuthProvider
        userManager={userManager}
        onSigninCallback={() => window.history.replaceState({}, '', window.location.pathname)}
      >
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={mainRouter} />
        </QueryClientProvider>
      </AuthProvider>
    )}
  </React.StrictMode>
)
